import { useParams, useSearchParams } from "react-router-dom";
import {
  getLandingPageConfig,
  getLandingPageConfigFromParams,
} from "../data/landingPageConfigs";
import { LandingPageConfig } from "../types/landingPage";
import { captureMessage } from "../utils/sentry";
import { RESERVED_PAGE_SEGMENTS } from "./usePartnerConfig";

/**
 * Resolve the landing-page id from the route. Two shapes are supported:
 *   - `/:id/*`                     (legacy, incl. sub-routes like `/copd-r/contact`)
 *   - `/:partnerSlug/:landingId/*` (partner path-prefix attribution)
 *
 * react-router ranks the two-segment route higher, so `/copd-r/direct-redirect`
 * matches it as `partnerSlug=copd-r, landingId=direct-redirect`. When the second
 * segment is a reserved page-type word, the first segment is actually the landing
 * id (legacy URL), so we return that instead.
 */
export function resolveLandingPageId(params: {
  id?: string;
  landingId?: string;
  partnerSlug?: string;
}): string | undefined {
  if (params.partnerSlug && params.landingId) {
    return RESERVED_PAGE_SEGMENTS.has(params.landingId)
      ? params.partnerSlug
      : params.landingId;
  }
  return params.id;
}

/**
 * Hook to get the landing page configuration based on the URL parameters
 * @returns The base landing page configuration
 */
export function useLandingPageConfig(): LandingPageConfig | undefined {
  const params = useParams<{
    id?: string;
    landingId?: string;
    partnerSlug?: string;
  }>();
  const landingPageId = resolveLandingPageId(params);
  const [searchParams] = useSearchParams();

  let pageConfig: LandingPageConfig | undefined;

  if (landingPageId) {
    pageConfig = getLandingPageConfig(landingPageId);

    // Case 2: ID exists but no predefined config, try dynamic params
    if (!pageConfig && searchParams.size > 0) {
      pageConfig = getLandingPageConfigFromParams(searchParams);
      if (pageConfig) {
        pageConfig.id = landingPageId; // Override the ID with the one from the URL
      }
    }

    // Log the warning for non-existent IDs with no valid configuration
    if (!pageConfig) {
      captureMessage(
        `Attempted to access non-existent landing page ID: ${landingPageId}`,
        "warning",
        {
          landing_page_id: landingPageId,
          has_search_params: searchParams.size > 0,
        },
      );
    }
  }
  // Case 3: No ID but has dynamic parameters
  else if (searchParams.size > 0) {
    pageConfig = getLandingPageConfigFromParams(searchParams);
  }
  // Case 4: No ID or params, e.g. a route (like /cond/:conditionId) that
  // isn't one of the /:id/* landing page shapes this hook resolves.

  return pageConfig;
}
